import { Button, Dialog, Divider } from '@blueprintjs/core'
import React, { FC, useEffect, useState } from 'react'
import packageJson from '../../../../package.json'
import { encryptPayload } from '../../aes-encryption'
import { jwtKeyStore } from '../../token-store'
import './modern-settings.css'

interface Activity {
  action: string
  status: string
  reason?: string
  context?: string
  createdAt: string
}

interface Props {
  isOpen: boolean
  toggle: () => void
  email: string
}

const UserActivity: FC<Props> = ({ isOpen, toggle, email }) => {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(false)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const token = jwtKeyStore.get() || ''
  const UTIL_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/utils'

  const CURRENT_VERSION = packageJson.version


  const fetchActivities = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${UTIL_URL}/get-user-activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': CURRENT_VERSION,
        }
      })

      if (response.ok) {
        const data = await response.json()
        setActivities(data.activities || [])
      } else {
        console.error('Failed to fetch activities')
        setActivities([])
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
      setActivities([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      void fetchActivities()
    }
  }, [isOpen])

  const sortedActivities = [...activities].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime()
    const dateB = new Date(b.createdAt).getTime()
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
  })

  const downloadActivities = () => {
    if (sortedActivities.length === 0) {
      return
    }

    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Activities for ${email}</title>
        </head>
        <body>
          <table border="1">
            <tr>
              <th><b>ACTION</b></th>
              <th><b>STATUS</b></th>
              <th><b>REASON/CONTEXT</b></th>
              <th><b>CREATED AT</b></th>
            </tr>
            ${sortedActivities.map(act => `
              <tr>
                <td>${act.action}</td>
                <td>${act.status}</td>
                <td>${act.reason || act.context || ''}</td>
                <td>${new Date(act.createdAt).toLocaleString()}</td>
              </tr>
            `).join('')}
          </table>
        </body>
      </html>
    `

    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `activities_${email}_${new Date().toISOString().split('T')[0]}.xls`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const refreshActivities = () => {
    void fetchActivities()
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={toggle}
      title="Your Activity"
      icon="history"
      className="modern-dialog"
      style={{ width: 600, height: '90vh', overflow: 'hidden' }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              padding: 16
            }}
          >
            Loading activities...
          </div>
        ) : (
          <>
            <div style={{ padding: '16px 24px 12px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#475569', fontSize: 13, fontWeight: 500 }}>
                    {sortedActivities.length} activities recorded ({sortOrder === 'asc' ? 'Oldest First' : 'Newest First'})
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    className="modern-btn-secondary modern-btn-icon"
                    icon="refresh"
                    onClick={refreshActivities}
                    title="Refresh Activities"
                  />
                  <Button
                    className="modern-btn-secondary modern-btn-icon"
                    icon={sortOrder === 'asc' ? 'sort-asc' : 'sort-desc'}
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    title="Toggle Sort Order"
                  />
                  <Button
                    className="modern-btn-secondary modern-btn-icon"
                    icon="download"
                    onClick={downloadActivities}
                    title="Download Activities"
                  />
                </div>
              </div>
            </div>
            <Divider style={{ margin: 0 }} />
            <div className="modern-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              {sortedActivities.length === 0 ? (
                <div style={{ marginTop: 40, color: '#94a3b8', textAlign: 'center', fontSize: '14px' }}>
                  No activities recorded for this user
                </div>
              ) : (
                <div className="modern-timeline" style={{ margin: '8px 0 8px 12px' }}>
                  {sortedActivities.map((act, idx) => {
                    const success = act.status === 'SUCCESS'

                    return (
                      <div key={idx} className="modern-timeline-item">
                        <div className={`modern-timeline-dot ${success ? 'success' : 'danger'}`} />
                        <div className="modern-timeline-content">
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                            <strong style={{ color: '#1e293b', fontSize: '14px', fontWeight: 600 }}>{act.action}</strong>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              color: success ? '#065f46' : '#991b1b',
                              backgroundColor: success ? '#d1fae5' : '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>{act.status}</span>
                          </div>

                          {(act.reason || act.context) && (
                            <div style={{ color: '#475569', fontSize: '13px', marginBottom: 6, lineHeight: 1.4 }}>
                              {act.reason || act.context}
                            </div>
                          )}

                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
                            {new Date(act.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default UserActivity
